import { Grid } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  GrievanceCommentExist,
  GrievanceLogbookForm,
  GrievanceSaveResult,
} from '../../../../services/types/Grievance';
import UIKitInput from '../../../../shared/UIKITComponents/Input';
import UIKitButton from '../../../../shared/UIKITComponents/Button';
import UIKitIcon from '../../../../shared/UIKITComponents/Icon';
import UIKitRadioGroup from '../../../../shared/UIKITComponents/RadioButtonGroup';
import Context from '../context/DetailsContext';
import SelectTemplateModal from './SelectTemplateModal';
import UploadFileModal from './UploadFileModal';
import UIKitIconButton from '../../../../shared/UIKITComponents/IconButton';
import MainContext from '../../../App/MainProvider/context';
import grievanceService from '../../../../services/grievanceService';
import { SpinnerArea } from '../../../../shared/components';
import UIKitConfirmModal from '../../../../shared/UIKITComponents/ConfirmModal';

const initialValues: GrievanceLogbookForm = {
  grievanceId: 0,
  documentTypeId: undefined,
  phaseId: -1,
  comment: '',
  fromTemplate: true,
  overrideComment: false,
};

const Logbook = () => {
  const { t } = useTranslation();
  const { service, notify } = useContext(MainContext);
  const { phases, lookupsDocumentTypes, grievance, reloadPhases } = useContext(Context);

  const [saving, setSaving] = useState(false);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);

  const [showConfirmOverrideModal, setShowConfirmOverrideModal] = useState(false);

  const currentPhaseIndex = phases.findIndex((phase) => phase.isCurrent);

  const phasesOptions = phases.map((phase, i) => ({
    text: t(`grievanceStages.${phase.phase}`),
    id: phase.phaseId,
    disabled: i > currentPhaseIndex,
  }));

  const onSave = (values: GrievanceLogbookForm, resetForm: () => void) => {
    service(() => grievanceService.logbook(grievance.number, values), {
      loader: setSaving,
      callback: (r: GrievanceSaveResult) => {
        let notificationMessage = t('saveSuccessfully');
        let notificationType: 'success' | 'warning' = 'success';
        if (r.documentsWarning) {
          notificationType = 'warning';
          notificationMessage += `\n\r${r.documentsWarning}`;
        }

        reloadPhases();

        notify(t('grievances'), notificationMessage, notificationType);
        resetForm();
        setSelectedDocument(null);
      },
    });
  };

  const handleBeforeSave = (values: GrievanceLogbookForm, resetForm: () => void) => {
    if (values.fromTemplate || !selectedDocument) {
      onSave(values, resetForm);
    } else {
      service(() => grievanceService.canSave(grievance.number, selectedDocument), {
        loader: setSaving,
        callback: (result: GrievanceCommentExist) => {
          if (!result.currentCommentId) {
            onSave(values, resetForm);
          } else if (result.canOverride) {
            setShowConfirmOverrideModal(true);
          } else {
            notify(
              t('grievances'),
              t('cantOverrideGrievanceDocument'),
              'danger',
              'exclamation-triangle'
            );
          }
        },
      });
    }
  };

  const formik = useFormik<GrievanceLogbookForm>({
    initialValues,
    validationSchema: Yup.object({
      phaseId: Yup.number().min(1, t('required')).required(t('required')),
      documentTypeId: Yup.number().nullable(),
      comment: Yup.string()
        .required(t('required'))
        .min(1, t('required'))
        .max(750, t('maxLengthCharacters', { length: 750 })),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }) => handleBeforeSave(values, resetForm),
  });

  const handleTemplateClick = () => {
    setShowTemplateModal(true);
  };

  const handleFileUploadClick = () => {
    setShowFileUploadModal(true);
  };

  const handleTemplateClose = () => {
    setShowTemplateModal(false);
  };

  const handleFileUploadClose = () => {
    setShowFileUploadModal(false);
  };

  const templateOptions = lookupsDocumentTypes
    .filter((type) => type.isTemplate)
    .map((type) => ({ text: type.name, id: type.id }));

  const fileUploadOptions = lookupsDocumentTypes.map((type) => ({ text: type.name, id: type.id }));

  const onSaveSelectedTemplate = (docTypeId: number) => {
    formik.setFieldValue('documentTypeId', docTypeId);
    formik.setFieldValue('fromTemplate', true);

    const doc = lookupsDocumentTypes.find((type) => type.id === docTypeId);

    const documentTypeCount = phases
      .flatMap((phase) => phase.logbookItems)
      .filter((item) => item.fromTemplate && item.documentTypeId === docTypeId).length;
    const suffix = documentTypeCount === 0 ? '' : ` ${documentTypeCount}`;

    setSelectedDocument(doc ? `${grievance.number} ${doc.name}${suffix}.docx` : null);
  };

  useEffect(
    () => () => {
      formik.resetForm();
      setSelectedDocument(null);
    },
    [grievance]
  );

  return (
    <SpinnerArea loading={saving}>
      <div className="logbook-wrapper">
        <h5>{t('logbook')}</h5>
        <div className="logbook-form">
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <UIKitInput
                  multiline
                  rows={4}
                  label={t('addComment')}
                  name="comment"
                  placeholder={t('typeYourComment')}
                  value={formik.values.comment}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.comment &&
                    !!formik.errors.comment &&
                    (formik.errors.comment !== t('required') ||
                      formik.values.documentTypeId !== undefined)
                  }
                  helperText={
                    formik.touched.comment &&
                    formik.errors.comment &&
                    (formik.errors.comment !== t('required') ||
                      formik.values.documentTypeId !== undefined)
                      ? formik.errors.comment
                      : undefined
                  }
                  fullWidth
                />
              </Grid>

              <Grid
                container
                direction="row"
                size={12}
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Grid>
                  <UIKitRadioGroup
                    name="phaseId"
                    elements={phasesOptions}
                    value={formik.values.phaseId}
                    onChange={(value) => formik.setFieldValue('phaseId', value)}
                    onBlur={formik.handleBlur}
                    color="secondary"
                    label={t('phase')}
                    error={
                      (!!formik.touched.phaseId || formik.values.documentTypeId !== undefined) &&
                      !!formik.errors.phaseId
                    }
                    helperText={
                      (formik.touched.phaseId || formik.values.documentTypeId !== undefined) &&
                      formik.errors.phaseId
                        ? formik.errors.phaseId
                        : undefined
                    }
                  />
                </Grid>
                <Grid className="grv-logbook-upload-buttons">
                  <UIKitButton
                    type="button"
                    text={t('createFromTemplate')}
                    variant="link"
                    iconLeft={<UIKitIcon name="add" />}
                    onClick={handleTemplateClick}
                    color="secondary"
                    size="small"
                    disabled={!!selectedDocument}
                  />

                  <UIKitButton
                    type="button"
                    text={t('uploadFile')}
                    variant="link"
                    iconLeft={<UIKitIcon name="upload_file" />}
                    onClick={handleFileUploadClick}
                    color="secondary"
                    size="small"
                    disabled={!!selectedDocument}
                  />
                </Grid>
              </Grid>
              {selectedDocument && (
                <Grid size={12}>
                  <span className="grv-logbook-selected-document">{t('selectedDocument')}</span>
                  <div className="grv-logbook-selected-document-content">
                    {selectedDocument}
                    <UIKitIconButton
                      iconName="delete_outline"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setSelectedDocument('');
                        formik.setFieldValue('documentTypeId', undefined);
                        formik.setFieldValue('formData', null);
                      }}
                    />
                  </div>
                </Grid>
              )}
              <Grid
                className="grv-milestone-submit-btn"
                style={{ display: 'flex', justifyContent: 'flex-end' }}
                size={12}
              >
                <UIKitButton
                  type="button"
                  disabled={!formik.isValid || !formik.dirty}
                  text={t('add')}
                  variant="flat"
                  onClick={() => formik.submitForm()}
                />
              </Grid>
            </Grid>
          </form>
        </div>

        <SelectTemplateModal
          isOpen={showTemplateModal}
          onClose={handleTemplateClose}
          onSave={onSaveSelectedTemplate}
          templateOptions={templateOptions}
        />

        <UploadFileModal
          isOpen={showFileUploadModal}
          onClose={handleFileUploadClose}
          onSave={(docId, formData) => {
            formik.setFieldValue('documentTypeId', docId);
            formik.setFieldValue('formData', formData);
            formik.setFieldValue('fromTemplate', false);

            const file = formData.get('file');
            setSelectedDocument(file ? (file as File).name : null);
          }}
          templateOptions={fileUploadOptions}
        />

        <UIKitConfirmModal
          open={showConfirmOverrideModal}
          onClose={() => setShowConfirmOverrideModal(false)}
          onConfirm={() => {
            onSave({ ...formik.values, overrideComment: true }, formik.resetForm);
          }}
          title={t('logbook')}
          message={t('grievanceConfirmingOverrideCommentMessage')}
        />
      </div>
    </SpinnerArea>
  );
};

export default Logbook;
